import "./Sidebar.css";
import logo from "../../assets/logos/nobgLogo.png";
import { 
  PlusCircle, 
  Mail, 
  Trash2,  
  UserCheck, 
  ShieldCheck, 
  LayoutGrid,   
  LogOut,
  Activity,
  CalendarDays,
  Globe
} from "lucide-react";
import { useWorkspacesContext } from "../../contexts/WorkspacesContext";
import { useNavigate, NavLink } from "react-router-dom";
import { useAuth } from '../../contexts/AuthContext';

const SIDEBAR_WIDTH = 280;

const getIcon = (workspace) => {
  return <Mail size={20} className="ms-2" />;
};  

export default function Sidebar({ onOpenModal }) {
  const { logout, user } = useAuth(); 
  const navigate = useNavigate();
  
  const canManage = user?.role === 'ROLE_ADMIN' || user?.role === 'ROLE_MANAGER' || user?.role === 'ROLE_TEAM_LEADER';
  
  const {
    workspaces,
    activeWorkspaceId,
    setActiveWorkspaceId,
    removeWorkspace,
  } = useWorkspacesContext();

  const handleLogout = () => {
    logout();           
    navigate('/login'); 
  };

  const handleDelete = (e, id, name) => {
    e.stopPropagation();
    const ok = window.confirm(`Delete workspace "${name || id}"?`);
    if (!ok) return;
    removeWorkspace(id);
  };

  return (
    <aside
      className="sidebar-custom bg-dark text-white position-fixed d-flex flex-column"
      style={{ width: SIDEBAR_WIDTH, height: "100vh", top: 0, left: 0, zIndex: 1050 }}
      aria-label="Sidebar"
    >
      {/* Header Logo */}
      <div className="p-3">
        <NavLink to="/" className="d-flex align-items-center mb-3 text-decoration-none">
          <span className="w-100">
            <img className="w-100 p-2" src={logo} alt="Check My Hits Logo" />
          </span>
        </NavLink>
      </div>

      <hr className="border-secondary my-2" />

      {/* Add account button*/}
      <div className="px-3 mb-3">
        <button
          type="button"
          onClick={onOpenModal}
          className="btn w-100 d-flex align-items-center justify-content-center fw-bold rounded-3 shadow-sm"
          style={{ backgroundColor: "#FFD700", color: "#002855" }}
        >
          <PlusCircle size={20} className="ms-2" />
          Add New account
        </button>
      </div>

      {/* Main Navigation */}
      <div className="px-3 overflow-y-auto">
        <div className="mb-3 px-2 py-1 rounded-3" style={{ backgroundColor: "rgba(255, 215, 0, 0.1)", borderLeft: "4px solid #FFD700" }}>
          <p className="small mb-0 text-white-50" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Welcome back,
          </p>
          <p className="mb-0 fw-bold text-warning" style={{ fontSize: 15 }}>
            {user?.username || user?.sub || 'User'}
          </p>
        </div>

        <p className="small mb-2 text-warning fw-bold text-uppercase" style={{ fontSize: 12, letterSpacing: '1px' }}>
          Main Menu
        </p>

        {/*Shared MASS  */}
        <NavLink
          to="/shared-mass"
          className="nav-link d-flex align-items-center w-100 px-2 py-2 mb-2 rounded-3"
          style={({ isActive }) => ({
            backgroundColor: isActive ? "#004085" : "transparent",
            color: isActive ? "#FFD700" : "#ffffff",
            textDecoration: "none",
            transition: "all 0.2s"
          })}
        >
          <LayoutGrid size={20} className="ms-2" />
          <span className="ms-2">Shared MASS</span>
          <span className="badge rounded-pill bg-success text-white ms-auto" style={{ fontSize: "0.7rem" }}>Live</span>
        </NavLink>

        {/*Accounts Manager - New Link for Listener Management */}
        <NavLink
          to="/accounts-management"
          className="nav-link d-flex align-items-center w-100 px-2 py-2 mb-2 rounded-3"
          style={({ isActive }) => ({
            backgroundColor: isActive ? "#004085" : "transparent",
            color: isActive ? "#FFD700" : "#ffffff",
            textDecoration: "none",
            transition: "all 0.2s"
          })}
        >
          <ShieldCheck size={20} className="ms-2" />
          <span className="ms-2">Accounts Manager</span>
        </NavLink>

        <NavLink
          to="/prod-vitals"
          className="nav-link d-flex align-items-center w-100 px-2 py-2 mb-2 rounded-3"
          style={({ isActive }) => ({
            backgroundColor: isActive ? "#004085" : "transparent",
            color: isActive ? "#FFD700" : "#ffffff",
            textDecoration: "none",
            transition: "all 0.2s"
          })}
        >
          <Activity size={20} className="ms-2" />
          <span className="ms-2">Prod Vitals</span>
        </NavLink>

        <NavLink
          to="/dns-checker"
          className="nav-link d-flex align-items-center w-100 px-2 py-2 mb-2 rounded-3"
          style={({ isActive }) => ({
            backgroundColor: isActive ? "#004085" : "transparent",
            color: isActive ? "#FFD700" : "#ffffff",
            textDecoration: "none",
            transition: "all 0.2s"
          })}
        >
          <Globe size={20} className="ms-2" />
          <span className="ms-2">DNS Checker</span>
        </NavLink>

        {canManage && (
          <NavLink
            to="/prod-vitals-history"
            className="nav-link d-flex align-items-center w-100 px-2 py-2 mb-2 rounded-3"
            style={({ isActive }) => ({
              backgroundColor: isActive ? "#004085" : "transparent",
              color: isActive ? "#FFD700" : "#ffffff",
              textDecoration: "none",
              transition: "all 0.2s"
            })}
          >
            <CalendarDays size={20} className="ms-2" />
            <span className="ms-2">Prod Vitals History</span>
          </NavLink>
        )}
            
        {/* Users Pending */}
        {canManage && (
          <NavLink
            to="/users"
            className="nav-link d-flex align-items-center w-100 px-2 py-2 mb-2 rounded-3"
            style={({ isActive }) => ({
              backgroundColor: isActive ? "#004085" : "transparent",
              color: isActive ? "#FFD700" : "#ffffff",
              textDecoration: "none",
              transition: "all 0.2s"
            })}
          >
            <UserCheck size={20} className="ms-2" />
            <span className="ms-2">Users Pending</span>
          </NavLink>
        )}

        <hr className="border-secondary my-3" />

        {/* Workspaces List */}
        <p className="small mb-2 text-warning fw-bold text-uppercase" style={{ fontSize: 12, letterSpacing: '1px' }}>
            My WorkSpaces
        </p>
        
        <ul className="nav nav-pills flex-column mb-auto">
          {workspaces.map((ws) => {
            const isActive = ws.id === activeWorkspaceId;
            const displayName = ws.accountName || ws.accountname || ws.email; 

            return (
              <li className="nav-item mb-1" key={ws.id}>
                <div
                  className="d-flex align-items-center w-100 px-2 py-1 rounded-3"
                  style={{
                    backgroundColor: isActive ? "#004085" : "transparent",
                    color: isActive ? "#FFD700" : "#ffffff",
                    transition: "all 0.2s",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                        setActiveWorkspaceId(ws.id);
                        navigate('/');
                      }}
                    className="nav-link flex-grow-1 d-flex align-items-center text-start p-2 border-0 bg-transparent"
                    style={{ color: "inherit" }}
                    title={ws.email}
                  >
                    {getIcon(ws)}
                    <span className="ms-2 text-truncate small">
                      {displayName}
                    </span>
                  </button>
                  
                  <button
                    type="button"
                    className="btn btn-sm opacity-50 hover-opacity-100"
                    onClick={(e) => handleDelete(e, ws.id, displayName)}
                    style={{ color: "inherit", border: 'none' }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            );
          })}
          
          {workspaces.length === 0 && (
            <li className="nav-item text-center text-secondary small px-3 mt-2 italic">
              No workspace found.
            </li>
          )}
        </ul>
      </div>

      <hr className="border-secondary my-2" />

      {/* Logout & Footer */}
      <div className="px-3 pb-3 mt-auto">
        <button 
          onClick={handleLogout} 
          className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center mb-3"
        >
          <LogOut size={18} className="me-2" /> Logout
        </button>
        
        <div className="text-center opacity-50" style={{ fontSize: 10 }}>
          Copyright ©2026 Check My Hits
          <br />
          Designed by{" "}
          <a className="text-warning text-decoration-none" href="https://t.me/ymoghar" target="_blank" rel="noreferrer">
            Moghar Yassine
          </a>
        </div>
      </div>
    </aside>
  );
}